import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  ArrowLeft,
  Sparkles,
  FileText,
  TrendingUp,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  BarChart3,
  CheckCircle2,
  XCircle,
  RefreshCw,
  AlertTriangle,
  GitCompare,
  Type as TypeIcon,
  History,
} from 'lucide-react';
import { theme } from '../styles/theme';
import { RefinedResponse } from '../types';
import HumanRefinementModal from './HumanRefinementModal';
import AIIcon from './AIIcon';
import {
  computeWordDiff,
  countWords,
  findLostEvidence,
  suggestRefinementWords,
  SpecificEvidence,
} from '../utils/refinementUtils';

const ComparisonContainer = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  margin-bottom: ${theme.spacing[6]};
  box-shadow: ${theme.shadows.md};
  transition: all ${theme.transitions.base};

  &:hover {
    box-shadow: ${theme.shadows.lg};
    transform: translateY(-1px);
  }
`;

const CardHeader = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary[50]}, ${theme.colors.accent[50]});
  border-bottom: 1px solid ${theme.colors.border};
  padding: ${theme.spacing[5]};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${theme.spacing[3]};
    align-items: flex-start;
  }
`;

const QuestionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  flex: 1;
`;

const QuestionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: ${theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.md};
  color: white;
`;

const QuestionContent = styled.div`
  flex: 1;

  h3 {
    margin: 0 0 ${theme.spacing[1]} 0;
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.semibold};
    font-size: ${theme.typography.fontSize.lg};
    line-height: ${theme.typography.lineHeight.tight};
  }

  p {
    margin: 0;
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const ImprovementBadge = styled.div<{ score?: number }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: ${({ score = 0 }) => {
    if (score >= 80) return theme.colors.success[100];
    if (score >= 60) return theme.colors.warning[100];
    if (score >= 40) return theme.colors.primary[100];
    return theme.colors.error[100];
  }};
  color: ${({ score = 0 }) => {
    if (score >= 80) return theme.colors.success[700];
    if (score >= 60) return theme.colors.warning[700];
    if (score >= 40) return theme.colors.primary[700];
    return theme.colors.error[700];
  }};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ComparisonBody = styled.div`
  padding: ${theme.spacing[6]};
`;

const ResponseComparison = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: ${theme.spacing[4]};
  align-items: stretch;

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[6]};
  }
`;

const ResponseSection = styled.div<{ type: 'original' | 'refined' }>`
  background: ${({ type }) => 
    type === 'original' 
      ? theme.colors.backgroundSecondary 
      : `linear-gradient(135deg, ${theme.colors.success[100]}20, ${theme.colors.primary[100]}20)`
  };
  border: 1px solid ${({ type }) => 
    type === 'original' 
      ? theme.colors.border 
      : theme.colors.primary[500] + '40'
  };
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${({ type }) => 
      type === 'original' 
        ? theme.colors.primary[300] 
        : theme.colors.success[500]
    };
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing[3]};
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  h4 {
    margin: 0;
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.semibold};
    font-size: ${theme.typography.fontSize.base};
  }
`;

const SectionIcon = styled.div<{ type: 'original' | 'refined' }>`
  color: ${({ type }) => 
    type === 'original' 
      ? theme.colors.primary[500] 
      : theme.colors.success[500]
  };
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.primary[100]};
    border-color: ${theme.colors.primary[400]};
    color: ${theme.colors.primary[600]};
  }
`;

const ResponseText = styled.div`
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${theme.colors.backgroundSecondary};
    border-radius: ${theme.borderRadius.full};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary[400]};
    border-radius: ${theme.borderRadius.full};
  }
`;

const ArrowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[4]} 0;

  @media (max-width: ${theme.breakpoints.md}) {
    transform: rotate(90deg);
    padding: 0;
  }
`;

const ArrowIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[500]});
  border-radius: ${theme.borderRadius.full};
  color: white;
  box-shadow: ${theme.shadows.md};
  animation: pulse 2s infinite;

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }
`;

const ImprovementSection = styled.div<{ expanded: boolean }>`
  margin-top: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.border};
  padding-top: ${theme.spacing[4]};
`;

const ImprovementHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.sm};
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.backgroundSecondary};
  }
`;

const ImprovementTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  h5 {
    margin: 0;
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.medium};
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover {
    color: ${theme.colors.primary[600]};
  }
`;

const ImprovementContent = styled.div<{ expanded: boolean }>`
  max-height: ${({ expanded }) => expanded ? 'none' : '0'};
  overflow: hidden;
  transition: all ${theme.transitions.base};
  padding: ${({ expanded }) => expanded ? `${theme.spacing[3]} 0` : '0'};
`;

const ImprovementText = styled.div`
  background: ${theme.colors.primary[50]};
  border: 1px solid ${theme.colors.primary[200]};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing[3]};
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const ImprovementActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${theme.spacing[3]};
  padding-top: ${theme.spacing[3]};
  border-top: 1px solid ${theme.colors.border};
`;

const HumanRefineButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: linear-gradient(135deg, ${theme.colors.accent[500]}, ${theme.colors.primary[500]});
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  box-shadow: ${theme.shadows.sm};

  &:hover {
    background: linear-gradient(135deg, ${theme.colors.accent[600]}, ${theme.colors.primary[600]});
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// ────────────────────────────────────────────────────────────────────────────
// AI Refine workflow — additional UI primitives
// ────────────────────────────────────────────────────────────────────────────

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
  align-items: center;
  justify-content: flex-end;
  margin-top: ${theme.spacing[3]};
`;

const ActionButton = styled.button<{ $variant?: 'accept' | 'reject' | 'regen' | 'neutral' | 'refine' }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  background: ${theme.colors.surface};
  color: ${theme.colors.textPrimary};

  &:hover { background: ${theme.colors.surfaceHover}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }

  ${({ $variant }) => {
    switch ($variant) {
      case 'accept':
        return `
          background: ${theme.colors.success[500]};
          border-color: ${theme.colors.success[500]};
          color: white;
          &:hover { background: ${theme.colors.success[600]}; border-color: ${theme.colors.success[600]}; }
        `;
      case 'reject':
        return `
          background: ${theme.colors.surface};
          border-color: ${theme.colors.error[500]};
          color: ${theme.colors.error[600]};
          &:hover { background: ${theme.colors.error[100]}; }
        `;
      case 'regen':
        return `
          background: ${theme.colors.surface};
          border-color: ${theme.colors.primary[400]};
          color: ${theme.colors.primary[700]};
          &:hover { background: ${theme.colors.primary[50]}; }
        `;
      case 'refine':
        return `
          background: linear-gradient(135deg, ${theme.colors.accent[500]}, ${theme.colors.primary[500]});
          border-color: transparent;
          color: white;
          box-shadow: ${theme.shadows.sm};
          &:hover {
            background: linear-gradient(135deg, ${theme.colors.accent[600]}, ${theme.colors.primary[600]});
            box-shadow: ${theme.shadows.md};
          }
        `;
      default:
        return '';
    }
  }}
`;

const StatusPill = styled.span<{ $tone: 'success' | 'warning' | 'error' | 'info' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  ${({ $tone }) => {
    switch ($tone) {
      case 'success':
        return `background:${theme.colors.success[100]};color:${theme.colors.success[700]};`;
      case 'warning':
        return `background:${theme.colors.warning[100]};color:${theme.colors.warning[700]};`;
      case 'error':
        return `background:${theme.colors.error[100]};color:${theme.colors.error[600]};`;
      case 'info':
        return `background:${theme.colors.primary[100]};color:${theme.colors.primary[700]};`;
      default:
        return `background:${theme.colors.backgroundSecondary};color:${theme.colors.textMuted};`;
    }
  }}
`;

const VersionBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[2]} ${theme.spacing[5]};
  background: ${theme.colors.backgroundSecondary};
  border-bottom: 1px solid ${theme.colors.border};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
`;

const VersionPill = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: ${theme.borderRadius.full};
  background: ${({ $active }) => ($active ? theme.colors.primary[600] : theme.colors.surface)};
  color: ${({ $active }) => ($active ? 'white' : theme.colors.textPrimary)};
  border: 1px solid ${({ $active }) => ($active ? theme.colors.primary[600] : theme.colors.border)};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  &:hover {
    background: ${({ $active }) => ($active ? theme.colors.primary[700] : theme.colors.surfaceHover)};
  }
`;

const WordCountPanel = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: ${theme.colors.backgroundSecondary};
  border-top: 1px solid ${theme.colors.border};
  font-size: ${theme.typography.fontSize.xs};
`;

const WCStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  .label {
    color: ${theme.colors.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 10px;
    font-weight: ${theme.typography.fontWeight.semibold};
  }
  .value {
    color: ${theme.colors.textPrimary};
    font-size: ${theme.typography.fontSize.base};
    font-weight: ${theme.typography.fontWeight.semibold};
    font-variant-numeric: tabular-nums;
  }
  .hint {
    color: ${theme.colors.textMuted};
    font-size: 10px;
  }
`;

const WCProgress = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  .label {
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: ${theme.colors.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: ${theme.typography.fontWeight.semibold};
  }
  .track {
    height: 6px;
    background: ${theme.colors.surface};
    border-radius: ${theme.borderRadius.full};
    overflow: hidden;
  }
  .fill {
    height: 100%;
    border-radius: ${theme.borderRadius.full};
    transition: width ${theme.transitions.base};
  }
`;

const FaithfulnessBanner = styled.div<{ $tone: 'warning' | 'success' }>`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: ${({ $tone }) =>
    $tone === 'warning' ? `${theme.colors.warning[100]}80` : `${theme.colors.success[100]}80`};
  color: ${({ $tone }) =>
    $tone === 'warning' ? theme.colors.warning[700] : theme.colors.success[700]};
  border-top: 1px solid ${theme.colors.border};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};

  .lost-list {
    margin: ${theme.spacing[1]} 0 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    list-style: none;
  }
  .lost-list li {
    background: ${({ $tone }) =>
      $tone === 'warning' ? theme.colors.warning[100] : theme.colors.success[100]};
    border: 1px solid ${({ $tone }) =>
      $tone === 'warning' ? theme.colors.warning[500] : theme.colors.success[500]};
    color: ${({ $tone }) =>
      $tone === 'warning' ? theme.colors.warning[700] : theme.colors.success[700]};
    font-family: ${theme.typography.fontFamily.mono};
    font-size: 11px;
    padding: 1px 8px;
    border-radius: ${theme.borderRadius.sm};
  }
`;

const ViewToggle = styled.div`
  display: inline-flex;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  background: ${theme.colors.surface};
`;

const ViewToggleButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: ${({ $active }) => ($active ? theme.colors.primary[100] : 'transparent')};
  color: ${({ $active }) => ($active ? theme.colors.primary[700] : theme.colors.textMuted)};
  border: none;
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  &:hover { background: ${({ $active }) => ($active ? theme.colors.primary[100] : theme.colors.surfaceHover)}; }
`;

const DiffSection = styled.div`
  padding: ${theme.spacing[5]};
  background: ${theme.colors.surface};
  border-top: 1px solid ${theme.colors.border};
`;

const DiffBody = styled.div`
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
  color: ${theme.colors.textPrimary};
  white-space: pre-wrap;
  max-height: 480px;
  overflow-y: auto;

  .added {
    background: ${theme.colors.success[100]};
    color: ${theme.colors.success[700]};
    padding: 0 2px;
    border-radius: 2px;
    text-decoration: none;
  }
  .removed {
    background: ${theme.colors.error[100]};
    color: ${theme.colors.error[600]};
    padding: 0 2px;
    border-radius: 2px;
    text-decoration: line-through;
  }
`;

const DiffLegend = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
  margin-bottom: ${theme.spacing[2]};
  align-items: center;
`;

const DiffLegendItem = styled.span<{ $tone: 'added' | 'removed' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &::before {
    content: '';
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    background: ${({ $tone }) =>
      $tone === 'added' ? theme.colors.success[500] : theme.colors.error[500]};
  }
`;

interface ResponseComparisonCardProps {
  refinedResponse: RefinedResponse;
  index: number;
  onHumanRefine?: (questionId: string, prompt: string, callbacks: {
    onChunk: (chunk: string) => void;
    onComplete: (finalResponse: string) => void;
    onError: (error: Error) => void;
  }) => void;
  onAcceptRefinement?: (questionId: string, refinedText: string) => void;
  /** Question metadata used for word-count targeting and faithfulness display. */
  questionType?: string;
  criterionLabel?: string;
  /** True when this run produced ungrounded scaffold drafts because the
   *  company knowledge base was empty. Renders a prominent per-card warning
   *  reminding the bidder to fill in bracketed placeholders. */
  isUngrounded?: boolean;
}

interface CardVersion {
  /** v1 = original; v2 = first AI refinement; v3+ = HITL refinements. */
  id: string;
  label: string;
  content: string;
  /** Free-form prompt the user typed; absent for v1 + v2 (AI-only versions). */
  prompt?: string;
  source: 'original' | 'ai_refinement' | 'hitl_refinement';
  timestamp: number;
}

type AcceptState = 'pending' | 'accepted' | 'rejected';

const ResponseComparisonCard: React.FC<ResponseComparisonCardProps> = ({
  refinedResponse,
  index,
  onHumanRefine,
  onAcceptRefinement,
  questionType,
  criterionLabel,
  isUngrounded = false,
}) => {
  const questionId = refinedResponse.question_id || `question_${index}`;

  // ── Version history (linear). v1 = original, v2 = first AI refinement
  // (props.refined_response), v3+ = each subsequent HITL refinement.
  // We keep the versions in component state so users can flip between them
  // without losing earlier output. ─────────────────────────────────────────
  const [versions, setVersions] = useState<CardVersion[]>(() => {
    const initial: CardVersion[] = [];
    if (refinedResponse.original_response) {
      initial.push({
        id: `${questionId}-v1`,
        label: 'v1',
        content: refinedResponse.original_response,
        source: 'original',
        timestamp: Date.now(),
      });
    }
    if (refinedResponse.refined_response) {
      initial.push({
        id: `${questionId}-v2`,
        label: 'v2',
        content: refinedResponse.refined_response,
        source: 'ai_refinement',
        timestamp: Date.now() + 1,
      });
    }
    return initial;
  });
  const [activeVersionIdx, setActiveVersionIdx] = useState<number>(
    versions.length > 1 ? 1 : 0
  );

  // Keep v1 / v2 in sync if upstream props change (e.g. backend re-pushes a
  // refinement for the same question), without nuking later HITL versions.
  useEffect(() => {
    setVersions((prev) => {
      const next = [...prev];
      const v1Idx = next.findIndex((v) => v.source === 'original');
      const v2Idx = next.findIndex((v) => v.source === 'ai_refinement');
      if (v1Idx >= 0 && refinedResponse.original_response) {
        next[v1Idx] = {
          ...next[v1Idx],
          content: refinedResponse.original_response,
        };
      }
      if (v2Idx >= 0 && refinedResponse.refined_response) {
        next[v2Idx] = {
          ...next[v2Idx],
          content: refinedResponse.refined_response,
        };
      }
      return next;
    });
  }, [refinedResponse.original_response, refinedResponse.refined_response]);

  const activeVersion = versions[activeVersionIdx] || versions[versions.length - 1];
  const originalContent = versions[0]?.content || refinedResponse.original_response || '';
  const activeContent = activeVersion?.content || '';

  // ── GENERATE-mode rendering ───────────────────────────────────────────
  // When the source tender had no inline answer for this question, the
  // backend drafts one from scratch and `original_response` arrives as ''.
  // The refine-vs-original comparison framing (split panels, arrow, diff
  // toggle, faithfulness check) doesn't apply — there's nothing to compare
  // *to*. Render a single AI-Drafted panel instead.
  const isDraftOnly = !(refinedResponse.original_response && refinedResponse.original_response.trim().length > 0);

  // ── Local UI state ────────────────────────────────────────────────────
  const [expandedImprovement, setExpandedImprovement] = useState(true);
  const [copiedOriginal, setCopiedOriginal] = useState(false);
  const [copiedRefined, setCopiedRefined] = useState(false);
  const [isRefinementModalOpen, setIsRefinementModalOpen] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [acceptState, setAcceptState] = useState<AcceptState>('pending');
  const [viewMode, setViewMode] = useState<'sideBySide' | 'diff'>('sideBySide');
  /** Last HITL prompt typed by the user — used for one-click Regenerate. */
  const [lastUserPrompt, setLastUserPrompt] = useState<string | null>(null);

  // ── Computed: word counts, target, faithfulness ───────────────────────
  const originalWordCount = useMemo(() => countWords(originalContent), [originalContent]);
  const activeWordCount = useMemo(() => countWords(activeContent), [activeContent]);
  const targetWordCount = useMemo(
    () => suggestRefinementWords(questionType, criterionLabel),
    [questionType, criterionLabel]
  );
  const wordDelta = activeWordCount - originalWordCount;
  const wordPctOfTarget = targetWordCount
    ? Math.min(150, Math.round((activeWordCount / targetWordCount) * 100))
    : null;

  /** Specific evidence in v1 (original) that's missing from the currently-active
   *  version. Triggers the faithfulness banner. */
  const lostEvidence: SpecificEvidence[] = useMemo(
    () => findLostEvidence(originalContent, activeContent),
    [originalContent, activeContent]
  );
  const hasFaithfulnessIssue =
    activeVersion?.source !== 'original' && lostEvidence.length > 0;

  /** Word-level diff for the diff-view tab. */
  const diffSegments = useMemo(
    () => computeWordDiff(originalContent, activeContent),
    [originalContent, activeContent]
  );

  // ── Helpers ──────────────────────────────────────────────────────────
  const handleCopyOriginal = async () => {
    await navigator.clipboard.writeText(originalContent);
    setCopiedOriginal(true);
    setTimeout(() => setCopiedOriginal(false), 2000);
  };

  const handleCopyRefined = async () => {
    await navigator.clipboard.writeText(activeContent);
    setCopiedRefined(true);
    setTimeout(() => setCopiedRefined(false), 2000);
  };

  const hasScore = (score: unknown): score is number =>
    typeof score === 'number' && Number.isFinite(score);

  const getImprovementIcon = (score?: number | null) => {
    if (!hasScore(score)) return <TrendingUp size={16} />;
    if (score >= 80) return <Sparkles size={16} />;
    if (score >= 60) return <TrendingUp size={16} />;
    return <BarChart3 size={16} />;
  };

  const getImprovementText = (score?: number | null) => {
    if (!hasScore(score)) return 'Score pending';
    if (score >= 80) return `Excellent (+${score}%)`;
    if (score >= 60) return `Good (+${score}%)`;
    if (score >= 40) return `Improved (+${score}%)`;
    return `Marginal (+${score}%)`;
  };

  // ── HITL refinement workflow ─────────────────────────────────────────
  const triggerRefinement = (prompt: string) => {
    if (!onHumanRefine) return;
    setIsRefining(true);
    setLastUserPrompt(prompt);
    let buffered = '';
    onHumanRefine(questionId, prompt, {
      onChunk: (chunk: string) => {
        buffered += chunk;
      },
      onComplete: (finalResponse: string) => {
        // Append a new version on completion so the user can compare; do NOT
        // overwrite v2 — earlier versions stay accessible via the version pills.
        const text = (finalResponse || buffered || '').trim();
        if (text) {
          setVersions((prev) => {
            const nextLabel = `v${prev.length + 1}`;
            const newVersion: CardVersion = {
              id: `${questionId}-${nextLabel}`,
              label: nextLabel,
              content: text,
              prompt,
              source: 'hitl_refinement',
              timestamp: Date.now(),
            };
            return [...prev, newVersion];
          });
          setActiveVersionIdx((prev) => prev + 1);
          setAcceptState('pending'); // new content needs a fresh accept/reject
        }
        setIsRefining(false);
      },
      onError: () => {
        setIsRefining(false);
      },
    });
  };

  const handleHumanRefine = (
    prompt: string,
    callbacks: {
      onChunk: (chunk: string) => void;
      onComplete: (finalResponse: string) => void;
      onError: (error: Error) => void;
    }
  ) => {
    if (!onHumanRefine) return;
    setIsRefining(true);
    setLastUserPrompt(prompt);

    const enhancedCallbacks = {
      ...callbacks,
      onComplete: (finalResponse: string) => {
        const text = (finalResponse || '').trim();
        if (text) {
          setVersions((prev) => {
            const nextLabel = `v${prev.length + 1}`;
            const newVersion: CardVersion = {
              id: `${questionId}-${nextLabel}`,
              label: nextLabel,
              content: text,
              prompt,
              source: 'hitl_refinement',
              timestamp: Date.now(),
            };
            return [...prev, newVersion];
          });
          setActiveVersionIdx((prev) => prev + 1);
          setAcceptState('pending');
        }
        callbacks.onComplete(finalResponse);
        setIsRefining(false);
      },
      onError: (error: Error) => {
        callbacks.onError(error);
        setIsRefining(false);
      },
    };

    onHumanRefine(questionId, prompt, enhancedCallbacks);
  };

  const handleAcceptFromModal = (refinedText: string) => {
    // Modal-driven accept: append the refinement as a new version AND mark accepted.
    setVersions((prev) => {
      const nextLabel = `v${prev.length + 1}`;
      const newVersion: CardVersion = {
        id: `${questionId}-${nextLabel}`,
        label: nextLabel,
        content: refinedText,
        source: 'hitl_refinement',
        prompt: lastUserPrompt || undefined,
        timestamp: Date.now(),
      };
      return [...prev, newVersion];
    });
    setActiveVersionIdx((prev) => prev + 1);
    setAcceptState('accepted');
    setIsRefinementModalOpen(false);
    if (onAcceptRefinement) {
      onAcceptRefinement(questionId, refinedText);
    }
  };

  // ── Accept / Reject / Regenerate handlers ────────────────────────────
  const handleAccept = () => {
    setAcceptState('accepted');
    if (onAcceptRefinement) {
      onAcceptRefinement(questionId, activeContent);
    }
  };

  const handleReject = () => {
    // Revert to v1 (original). Subsequent HITL versions remain accessible
    // via the version pills if the user changes their mind.
    setAcceptState('rejected');
    setActiveVersionIdx(0);
  };

  const handleRegenerate = () => {
    if (!onHumanRefine) return;
    // If the user has previously typed a HITL prompt, re-run with the same
    // intent. Otherwise, request a fresh generic refinement. The default
    // prompt differs by mode: refine mode preserves evidence from the v1
    // original; draft-only mode has no original so the prompt steers a
    // different angle on the existing draft instead.
    const refineDefault =
      'Try a different angle. Preserve every concrete fact, name, project, award, date, and number from the original — do not delete evidence. Avoid generic closing tags like "value for money".';
    const draftDefault =
      'Try a different angle. Stay grounded in the company knowledge base — keep every concrete fact, name, project, award, date, and number that came from KB context. Avoid generic closing tags like "value for money".';
    const prompt = lastUserPrompt || (isDraftOnly ? draftDefault : refineDefault);
    triggerRefinement(prompt);
  };

  const isOriginalActive = activeVersion?.source === 'original';

  return (
    <ComparisonContainer>
      <CardHeader>
        <HeaderContent>
          <QuestionInfo>
            <QuestionIcon>
              <FileText size={24} />
            </QuestionIcon>
            <QuestionContent>
              <h3>Question {index + 1}</h3>
              <p>{refinedResponse.question}</p>
            </QuestionContent>
          </QuestionInfo>
          
          <div style={{ display: 'flex', gap: theme.spacing[2], alignItems: 'center', flexWrap: 'wrap' }}>
            {acceptState === 'accepted' && (
              <StatusPill $tone="success">
                <CheckCircle2 size={12} /> Accepted
              </StatusPill>
            )}
            {acceptState === 'rejected' && (
              <StatusPill $tone="error">
                <XCircle size={12} /> Rejected — showing original
              </StatusPill>
            )}
            <ImprovementBadge
              score={
                typeof refinedResponse.improvement_score === 'number'
                  ? refinedResponse.improvement_score
                  : undefined
              }
            >
              {getImprovementIcon(refinedResponse.improvement_score)}
              {getImprovementText(refinedResponse.improvement_score)}
            </ImprovementBadge>
          </div>
        </HeaderContent>
      </CardHeader>

      {/* Ungrounded-drafts warning — appears once per card when the run
          produced scaffolded drafts because the company KB was empty. The
          severity is intentionally high (red border, AlertTriangle icon)
          because the LLM had no company-specific evidence to cite, so the
          response contains [BRACKETED PLACEHOLDERS] the bidder MUST fill in
          before submission. Do not soften this. */}
      {isUngrounded && (
        <div
          role="alert"
          style={{
            margin: theme.spacing[4],
            padding: theme.spacing[4],
            background: `${theme.colors.error[100]}66`,
            border: `1px solid ${theme.colors.error[500]}80`,
            borderRadius: theme.borderRadius.md,
            color: theme.colors.error[700],
            display: 'flex',
            gap: theme.spacing[3],
            alignItems: 'flex-start',
            fontSize: theme.typography.fontSize.sm,
            lineHeight: theme.typography.lineHeight.relaxed,
          }}
        >
          <AlertTriangle size={20} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <strong>Ungrounded scaffold — your knowledge base was empty.</strong>
            <br />
            This draft contains <strong>[BRACKETED PLACEHOLDERS]</strong> instead of
            real company specifics (certifications, case studies, named team
            members, project counts). Replace each placeholder with your actual
            evidence before submission. To get a fully grounded draft next time,
            upload reference documents to your knowledge base and re-run.
          </div>
        </div>
      )}

      {/* Version history bar — v1 (original), v2 (AI), v3+ (HITL refinements) */}
      {versions.length > 1 && (
        <VersionBar>
          <History size={12} />
          <span style={{ marginRight: theme.spacing[2] }}>Versions:</span>
          {versions.map((v, i) => (
            <VersionPill
              key={v.id}
              $active={i === activeVersionIdx}
              onClick={() => setActiveVersionIdx(i)}
              title={
                v.source === 'original'
                  ? 'Original bidder response'
                  : v.source === 'ai_refinement'
                  ? 'Initial AI refinement'
                  : v.prompt
                  ? `HITL: "${v.prompt.slice(0, 80)}${v.prompt.length > 80 ? '…' : ''}"`
                  : 'HITL refinement'
              }
            >
              {v.label}
            </VersionPill>
          ))}
          {/* Side-by-side / Diff toggle is suppressed in draft-only mode —
              both views frame the response as a comparison against v1, but
              there is no v1 when the original_response is empty. */}
          {!isDraftOnly && (
            <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
              <ViewToggle>
                <ViewToggleButton
                  $active={viewMode === 'sideBySide'}
                  onClick={() => setViewMode('sideBySide')}
                >
                  <FileText size={12} /> Side-by-side
                </ViewToggleButton>
                <ViewToggleButton
                  $active={viewMode === 'diff'}
                  onClick={() => setViewMode('diff')}
                >
                  <GitCompare size={12} /> Diff
                </ViewToggleButton>
              </ViewToggle>
            </span>
          )}
        </VersionBar>
      )}

      {/* Faithfulness warning — surfaced when the active version drops specific
          evidence from v1 (proper nouns, awards, dates, currency, %, named projects).
          Suppressed in draft-only mode: there is no v1 to be faithful to. */}
      {hasFaithfulnessIssue && viewMode === 'sideBySide' && !isDraftOnly && (
        <FaithfulnessBanner $tone="warning">
          <AlertTriangle size={18} style={{ marginTop: 2, flexShrink: 0 }} />
          <div>
            <strong>
              {lostEvidence.length} specific{' '}
              {lostEvidence.length === 1 ? 'claim' : 'claims'} from the original
              {lostEvidence.length === 1 ? ' is' : ' are'} missing in this refinement.
            </strong>
            <br />
            Restore them via "Restore lost evidence" in AI Refine before submitting.
            <ul className="lost-list">
              {lostEvidence.slice(0, 12).map((e, i) => (
                <li key={`${e.token}-${i}`} title={e.kind}>
                  {e.token}
                </li>
              ))}
              {lostEvidence.length > 12 && (
                <li style={{ background: 'transparent', border: 'none' }}>
                  +{lostEvidence.length - 12} more
                </li>
              )}
            </ul>
          </div>
        </FaithfulnessBanner>
      )}

      <ComparisonBody>
        {isDraftOnly ? (
          /* Draft-only layout: no original to compare against, so render a
             single panel with the AI-authored draft. The diff view and the
             "Original (reference)" pane don't apply — there's no v1. */
          <ResponseSection type="refined">
            <SectionHeader>
              <SectionTitle>
                <SectionIcon type="refined">
                  <AIIcon size={18} />
                </SectionIcon>
                <h4>
                  {activeVersion?.source === 'hitl_refinement'
                    ? `Your Refinement (${activeVersion.label})`
                    : 'AI-Drafted Response'}
                </h4>
              </SectionTitle>
              <CopyButton onClick={handleCopyRefined}>
                {copiedRefined ? <Check size={14} /> : <Copy size={14} />}
              </CopyButton>
            </SectionHeader>
            <ResponseText>{activeContent}</ResponseText>
          </ResponseSection>
        ) : viewMode === 'sideBySide' ? (
          <ResponseComparison>
            {/* Refined first — that's the version the bidder will ship.
                Original sits on the right as reference. The arrow now
                points right→left to convey "we transformed THAT into
                THIS". */}
            <ResponseSection type="refined">
              <SectionHeader>
                <SectionTitle>
                  <SectionIcon type="refined">
                    <AIIcon size={18} />
                  </SectionIcon>
                  <h4>
                    {isOriginalActive
                      ? 'Original Response'
                      : activeVersion?.source === 'hitl_refinement'
                      ? `Your Refinement (${activeVersion.label})`
                      : 'AI Enhanced Response'}
                  </h4>
                </SectionTitle>
                <CopyButton onClick={handleCopyRefined}>
                  {copiedRefined ? <Check size={14} /> : <Copy size={14} />}
                </CopyButton>
              </SectionHeader>
              <ResponseText>{activeContent}</ResponseText>
            </ResponseSection>

            <ArrowContainer>
              <ArrowIcon title="Refined from the original on the right">
                <ArrowLeft size={24} />
              </ArrowIcon>
            </ArrowContainer>

            <ResponseSection type="original">
              <SectionHeader>
                <SectionTitle>
                  <SectionIcon type="original">
                    <FileText size={18} />
                  </SectionIcon>
                  <h4>Original (reference)</h4>
                </SectionTitle>
                <CopyButton onClick={handleCopyOriginal}>
                  {copiedOriginal ? <Check size={14} /> : <Copy size={14} />}
                </CopyButton>
              </SectionHeader>
              <ResponseText>{originalContent}</ResponseText>
            </ResponseSection>
          </ResponseComparison>
        ) : (
          <DiffSection>
            <DiffLegend>
              <span>Comparing v1 (original) to {activeVersion?.label || 'current'}.</span>
              <DiffLegendItem $tone="added">added</DiffLegendItem>
              <DiffLegendItem $tone="removed">removed</DiffLegendItem>
            </DiffLegend>
            <DiffBody>
              {diffSegments.map((seg, i) => {
                if (seg.op === 'equal') return <span key={i}>{seg.text}</span>;
                if (seg.op === 'added')
                  return (
                    <span key={i} className="added">
                      {seg.text}
                    </span>
                  );
                return (
                  <span key={i} className="removed">
                    {seg.text}
                  </span>
                );
              })}
            </DiffBody>
          </DiffSection>
        )}

        {/* Word count panel — original / current / target / progress vs target.
            In draft-only mode the "Original" stat is suppressed (always 0) and
            the "Current" hint loses its "vs original" qualifier. */}
        <WordCountPanel>
          {!isDraftOnly && (
            <WCStat>
              <span className="label">Original</span>
              <span className="value">{originalWordCount}</span>
              <span className="hint">words</span>
            </WCStat>
          )}
          <WCStat>
            <span className="label">
              {isDraftOnly ? 'Drafted' : 'Current'}
              {activeVersion && !isDraftOnly ? ` · ${activeVersion.label}` : ''}
            </span>
            <span className="value">{activeWordCount}</span>
            <span className="hint">
              {isDraftOnly
                ? 'words'
                : wordDelta === 0
                ? 'no change'
                : `${wordDelta > 0 ? '+' : ''}${wordDelta} vs original`}
            </span>
          </WCStat>
          {targetWordCount && (
            <WCStat>
              <span className="label">Target</span>
              <span className="value">~{targetWordCount}</span>
              <span className="hint">
                {questionType ? `${questionType}` : ''}
                {criterionLabel ? ` · ${criterionLabel}` : ''}
              </span>
            </WCStat>
          )}
          {wordPctOfTarget !== null && (
            <WCProgress>
              <span className="label">
                <span>Length vs target</span>
                <span>
                  {wordPctOfTarget}% of ~{targetWordCount}
                </span>
              </span>
              <span className="track">
                <span
                  className="fill"
                  style={{
                    display: 'block',
                    width: `${Math.min(100, wordPctOfTarget)}%`,
                    background:
                      wordPctOfTarget < 60
                        ? theme.colors.warning[500]
                        : wordPctOfTarget > 130
                        ? theme.colors.error[500]
                        : theme.colors.success[500],
                  }}
                />
              </span>
            </WCProgress>
          )}
        </WordCountPanel>

        {/* Improvement Details — default-open; the rubric scorer's grounded
            summary is the most useful information on this card so don't hide
            it behind a click. Faithfulness pill surfaces a clean state when
            no specific evidence was lost from the original. */}
        <ImprovementSection expanded={expandedImprovement}>
          <ImprovementHeader onClick={() => setExpandedImprovement(!expandedImprovement)}>
            <ImprovementTitle>
              <Lightbulb size={16} color={theme.colors.warning[600]} />
              <h5>Improvement Details</h5>
              {!hasFaithfulnessIssue && !isOriginalActive && !isDraftOnly && (
                <span style={{ marginLeft: 8 }}>
                  <StatusPill $tone="success">
                    <CheckCircle2 size={12} /> Faithful
                  </StatusPill>
                </span>
              )}
            </ImprovementTitle>
            <ToggleButton>
              {expandedImprovement ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </ToggleButton>
          </ImprovementHeader>

          <ImprovementContent expanded={expandedImprovement}>
            <ImprovementText>
              {(activeVersion?.source === 'hitl_refinement' && activeVersion.prompt
                ? `You requested: "${activeVersion.prompt}". `
                : '') +
                (refinedResponse.improvement_summary ||
                  refinedResponse.refinement_reason ||
                  (isOriginalActive
                    ? 'Showing the original bidder response — no AI changes applied.'
                    : 'No grounded improvement summary available for this version.'))}
            </ImprovementText>
              
              <ActionRow>
                <ActionButton
                  $variant="accept"
                  onClick={handleAccept}
                  disabled={isOriginalActive || acceptState === 'accepted'}
                  title="Lock in this version as the response for submission"
                >
                  <CheckCircle2 size={14} /> Accept
                </ActionButton>
                <ActionButton
                  $variant="reject"
                  onClick={handleReject}
                  disabled={isOriginalActive || acceptState === 'rejected' || isDraftOnly}
                  title={
                    isDraftOnly
                      ? 'Reject is disabled in draft-only mode — there is no original response to revert to. Use Regenerate or AI Refine for a different draft.'
                      : 'Revert to the original bidder response'
                  }
                >
                  <XCircle size={14} /> Reject
                </ActionButton>
                <ActionButton
                  $variant="regen"
                  onClick={handleRegenerate}
                  disabled={isRefining || !onHumanRefine}
                  title="Regenerate using the same prompt (or a sensible default)"
                >
                  <RefreshCw size={14} /> {isRefining ? 'Regenerating…' : 'Regenerate'}
                </ActionButton>
                <ActionButton
                  $variant="refine"
                  onClick={() => setIsRefinementModalOpen(true)}
                  disabled={isRefining || !onHumanRefine}
                >
                  <AIIcon size={14} /> {isRefining ? 'Refining…' : 'AI Refine'}
                </ActionButton>
              </ActionRow>
            </ImprovementContent>
          </ImprovementSection>
      </ComparisonBody>
      
      <HumanRefinementModal
        isOpen={isRefinementModalOpen}
        onClose={() => setIsRefinementModalOpen(false)}
        question={refinedResponse.question}
        currentResponse={activeContent}
        onRefine={handleHumanRefine}
        onAcceptRefinement={handleAcceptFromModal}
        isLoading={isRefining}
      />
    </ComparisonContainer>
  );
};

export default ResponseComparisonCard;